import dayjs from 'dayjs'
import { useSelector } from 'react-redux'
import AccessTimeRoundedIcon from '@mui/icons-material/AccessTimeRounded'
import ConfirmationNumberRoundedIcon from '@mui/icons-material/ConfirmationNumberRounded'
import CallRoundedIcon from '@mui/icons-material/CallRounded'
import FiberNewRoundedIcon from '@mui/icons-material/FiberNewRounded'
import { getMeetingSrcIcon, getMeetingSrcLabel } from 'src/consumer/helpers/meetingSrcUtils'
import { getAgeFromDobWithTime } from 'src/consumer/helpers/profilePageHelper'
import { formatPhoneNumber, getExactTimeDiff } from 'src/consumer/helpers/utilFunctions'
import { preparePatientLabelFromPatientInfoObject } from 'src/supportPortal/views/scheduledPatients/utils'
import { selectAppointmentCalendarSettings } from 'src/clinic/reducers/prescription/prescriptionReducer'
import { getBookedSlotStripColor } from 'src/clinic/components/ClinicManageBookingsPage/utils/bookedSlotColorUtils'
import { shouldShowPrescriptionPdfDownloadButtons } from 'src/clinic/components/ClinicManageBookingsPage/utils'
import InTherapyButton from 'src/clinic/components/ClinicManageBookingsPage/slotItem/component/InTherapyButton'
import ConsultsActions from 'src/clinic/components/ClinicManageBookingsPage/consultsTab/components/ConsultsActions'
import ConsultsDetailsCell, {
  SessionStatusChip,
} from 'src/clinic/components/ClinicManageBookingsPage/consultsTab/components/ConsultsDetailsCell'
import ConsultsVitalsCell from 'src/clinic/components/ClinicManageBookingsPage/consultsTab/components/ConsultsVitalsCell'

// ── Small colour helpers so the left rail / wait tag derive from the same status colour the
//    card outline uses (getBookedSlotStripColor → borderColor). ─────────────────────────────────

/** Black or white text for readability over a solid hex background (WCAG luminance). */
function pickTextColor(hex) {
  if (!hex || hex[0] !== '#' || hex.length < 7) return '#ffffff'
  const r = parseInt(hex.slice(1, 3), 16)
  const g = parseInt(hex.slice(3, 5), 16)
  const b = parseInt(hex.slice(5, 7), 16)
  const lin = (v) => {
    const s = v / 255
    return s <= 0.03928 ? s / 12.92 : Math.pow((s + 0.055) / 1.055, 2.4)
  }
  const luminance = 0.2126 * lin(r) + 0.7152 * lin(g) + 0.0722 * lin(b)
  return luminance < 0.5 ? '#ffffff' : '#1f2937'
}

/** Translucent tint of a hex colour, e.g. for the wait tag that complements the left rail. */
function hexToRgba(hex, alpha) {
  if (!hex || hex[0] !== '#' || hex.length < 7) return `rgba(99, 102, 241, ${alpha})`
  const r = parseInt(hex.slice(1, 3), 16)
  const g = parseInt(hex.slice(3, 5), 16)
  const b = parseInt(hex.slice(5, 7), 16)
  return `rgba(${r}, ${g}, ${b}, ${alpha})`
}

/**
 * Desktop appointment card for the mentor Appointments tab — a three-part strip:
 *
 *   [ left rail ]  |  [ patient + clinical details ]  |  [ actions ]
 *
 * • Left rail is a colour block tinted with the card's status/outline colour (session status,
 *   payment, emergency …) and carries the at-a-glance timing: time, token no. and mode.
 * • Middle uses the full width for the patient (name is the loudest element), demographics,
 *   optional phone (opt-in, no copy button), complaint/diagnosis, screening steps and in-therapy.
 * • Right holds only the row actions — there is no Start/Join button; clicking anywhere on the
 *   card opens the session page.
 * • Wait time floats as a corner tag in a colour that complements the left rail.
 */
export default function ConsultsDesktopCard({
  bookedSlot,
  viewButtonClick,
  showActionButtons = false,
  hidePatientContactDetails = false,
  mentorProfileData,
  clinicLocationData,
  handleNewTherapyRequest,
  setClickedBookedSlotData,
  therapyBookingDataForThisEvent,
  setEditPatientDialogOpen,
  forMentor,
  setAddVitalsDialogOpen,
  setEditBookingDialogOpen,
  refreshBookedSlots,
  prescriptionViewSettings,
  sendPrescriptionPdfApiSignature,
  prescriptionGetApiSignature,
  prescriptionSaveApiSignature,
  fileUploadApiSignature,
  showGalleryForPrescriptionImages,
  sendSecureSessionLinkToPatientSignature,
  sendPublicSessionLinkToPatientSignature,
  prescriptionImagesGetApiSignature,
  prescriptionImagesSaveApiSignature,
}) {
  const patientInfo = bookedSlot?.patientInfo
  const visitNo = bookedSlot?.patientMeetingSeqNumber
  const meetingSrc = bookedSlot?.meetingSrc
  const slotStart = bookedSlot?.startTime ? dayjs.utc(bookedSlot.startTime).local() : null
  const canJoin = Boolean(viewButtonClick)

  // Outline / rail colour from the shared status→colour map (status, payment, emergency).
  const stripColor = getBookedSlotStripColor(bookedSlot)
  const railColor = stripColor.borderColor
  const railText = pickTextColor(railColor)

  // Practitioner's booking-listing display preferences.
  const apptCalendarSettings = useSelector(selectAppointmentCalendarSettings)?.data
  const showAgeGender = apptCalendarSettings?.showPatientAgeAndGender ?? true
  const showVisits = apptCalendarSettings?.showPatientVisits ?? true

  // First visit → new patient. Shown as a loud tag; the plain "Visit #1" is then redundant.
  const isNewPatient = visitNo === 1
  const age = getAgeFromDobWithTime(patientInfo?.dateOfBirth)
  const genderInitial = patientInfo?.gender ? String(patientInfo.gender).charAt(0).toUpperCase() : ''
  const ageGender = [age, genderInitial].filter((v) => v != null && v !== '' && v !== 'NaN').join('/')
  const metaBits = [
    showAgeGender && ageGender ? ageGender : null,
    patientInfo?.patientNumber ? `ID ${patientInfo.patientNumber}` : null,
    showVisits && visitNo && !isNewPatient ? `Visit #${visitNo}` : null,
  ].filter(Boolean)

  // Phone is opt-in (backend flag) and still respects the hard contact-privacy gates.
  const contactHidden =
    hidePatientContactDetails || bookedSlot?.patientContactDetailsVisible === false
  const showPhone =
    (apptCalendarSettings?.showPatientPhoneNumber ?? false) &&
    !contactHidden &&
    Boolean(patientInfo?.mobile)
  const phone = showPhone ? formatPhoneNumber(patientInfo.mobile) : null
  const phoneLabel = phone
    ? phone.formattedPhoneNumber
      ? `${phone.countryCode ? `(${phone.countryCode}) ` : ''}${phone.formattedPhoneNumber}`
      : phone.concatenatedString
    : null

  // Wait time: actual (slot → session start) once started, running otherwise.
  let waitContent = '--'
  const sessionStart = bookedSlot?.sessionStartedAt
    ? dayjs.utc(bookedSlot.sessionStartedAt).local()
    : null
  if (sessionStart && slotStart) {
    waitContent = getExactTimeDiff(slotStart, sessionStart, false, false, false)
  } else if (slotStart && slotStart.isSame(dayjs(), 'day')) {
    waitContent = getExactTimeDiff(slotStart, dayjs(), false, false, false)
  }
  const showWait = (apptCalendarSettings?.showWaitingTime ?? true) && waitContent !== '--'

  const onOpenVitals = setAddVitalsDialogOpen
    ? (slot) => {
        setClickedBookedSlotData?.(slot)
        setAddVitalsDialogOpen(true)
      }
    : undefined

  const actions = showActionButtons ? (
    <ConsultsActions
      bookedSlot={bookedSlot}
      mentorProfileData={mentorProfileData}
      clinicLocationData={clinicLocationData}
      handleNewTherapyRequest={handleNewTherapyRequest}
      setClickedBookedSlotData={setClickedBookedSlotData}
      therapyBookingDataForThisEvent={therapyBookingDataForThisEvent}
      setEditPatientDialogOpen={setEditPatientDialogOpen}
      forMentor={forMentor}
      setAddVitalsDialogOpen={setAddVitalsDialogOpen}
      setEditBookingDialogOpen={setEditBookingDialogOpen}
      refreshBookedSlots={refreshBookedSlots}
      showPrescriptionPdfDownloadButtons={shouldShowPrescriptionPdfDownloadButtons({
        forMentor,
        bookedSlot,
        prescriptionViewSettingsData: prescriptionViewSettings,
      })}
      sendPrescriptionPdfApiSignature={sendPrescriptionPdfApiSignature}
      prescriptionGetApiSignature={prescriptionGetApiSignature}
      prescriptionSaveApiSignature={prescriptionSaveApiSignature}
      fileUploadApiSignature={fileUploadApiSignature}
      showGalleryForPrescriptionImages={showGalleryForPrescriptionImages}
      sendSecureSessionLinkToPatientSignature={sendSecureSessionLinkToPatientSignature}
      sendPublicSessionLinkToPatientSignature={sendPublicSessionLinkToPatientSignature}
      prescriptionImagesGetApiSignature={prescriptionImagesGetApiSignature}
      prescriptionImagesSaveApiSignature={prescriptionImagesSaveApiSignature}
    />
  ) : null

  return (
    <div
      role={canJoin ? 'button' : undefined}
      tabIndex={canJoin ? 0 : undefined}
      onClick={canJoin ? () => viewButtonClick(bookedSlot) : undefined}
      onKeyDown={
        canJoin
          ? (e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault()
                viewButtonClick(bookedSlot)
              }
            }
          : undefined
      }
      style={{
        position: 'relative',
        display: 'flex',
        alignItems: 'stretch',
        gap: '14px',
        border: `1.5px solid ${railColor}`,
        borderRadius: '14px',
        background: '#ffffff',
        boxShadow: '0 1px 3px rgba(15, 23, 42, 0.08)',
        padding: '10px',
        cursor: canJoin ? 'pointer' : 'default',
        overflow: 'hidden',
      }}
    >
      {/* ── LEFT RAIL: time · token · mode, on the status colour ──────────────────────────────── */}
      <div
        style={{
          flexShrink: 0,
          width: '116px',
          background: railColor,
          color: railText,
          borderRadius: '10px',
          padding: '10px 8px',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '7px',
          textAlign: 'center',
        }}
      >
        <span
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '4px',
            fontWeight: 800,
            fontSize: '17px',
            lineHeight: 1.1,
            whiteSpace: 'nowrap',
          }}
        >
          <AccessTimeRoundedIcon sx={{ fontSize: 16, opacity: 0.85 }} />
          {slotStart ? slotStart.format('h:mm A') : '--'}
        </span>
        {bookedSlot?.appointmentNumber != null && (
          <span
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '3px',
              fontWeight: 700,
              fontSize: '13px',
              padding: '2px 9px',
              borderRadius: '999px',
              background: hexToRgba('#ffffff', railText === '#ffffff' ? 0.22 : 0.55),
              whiteSpace: 'nowrap',
            }}
            title={`Token #${bookedSlot.appointmentNumber}`}
          >
            <ConfirmationNumberRoundedIcon sx={{ fontSize: 13 }} />
            {bookedSlot.appointmentNumber}
          </span>
        )}
        <span
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '4px',
            fontSize: '12px',
            fontWeight: 600,
            whiteSpace: 'nowrap',
          }}
        >
          {getMeetingSrcIcon(meetingSrc, { size: 15, color: railText })}
          {getMeetingSrcLabel(meetingSrc)}
        </span>
      </div>

      {/* ── MIDDLE: patient + clinical details (uses the full width) ──────────────────────────── */}
      <div
        style={{
          flex: 1,
          minWidth: 0,
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          gap: '5px',
          paddingBottom: showWait ? '16px' : 0,
        }}
      >
        {/* Name row — patient name is the most prominent element */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap', minWidth: 0 }}>
          {bookedSlot?.therapyPatient && <InTherapyButton bookedSlot={bookedSlot} slotBlocked={false} />}
          <span
            style={{
              fontWeight: 800,
              fontSize: '16px',
              color: '#0f172a',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              maxWidth: '100%',
            }}
          >
            {preparePatientLabelFromPatientInfoObject(patientInfo, true)}
          </span>
          {isNewPatient && (
            <span
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '3px',
                fontSize: '11px',
                fontWeight: 800,
                letterSpacing: '0.2px',
                color: '#ffffff',
                background: '#7c3aed',
                padding: '2px 9px 2px 6px',
                borderRadius: '999px',
                whiteSpace: 'nowrap',
                textTransform: 'uppercase',
              }}
              title="First visit — new patient"
            >
              <FiberNewRoundedIcon style={{ fontSize: 16 }} />
              New patient
            </span>
          )}
          <SessionStatusChip bookedSlot={bookedSlot} />
        </div>

        {/* Demographics + optional phone */}
        {(metaBits.length > 0 || phoneLabel) && (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '10px',
              flexWrap: 'wrap',
              fontSize: '12.5px',
              color: '#64748b',
            }}
          >
            {metaBits.length > 0 && <span>{metaBits.join(' · ')}</span>}
            {phoneLabel && (
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', color: '#475569' }}>
                <CallRoundedIcon sx={{ fontSize: 14 }} />
                {phoneLabel}
              </span>
            )}
          </div>
        )}

        {/* Type + badges + complaint/diagnosis + screening steps (full width for Cc/Dx) */}
        <ConsultsDetailsCell bookedSlot={bookedSlot} compact diagnosisMaxWidth={440} />
      </div>

      {/* ── RIGHT: actions only (no Start button; the whole card opens the session) ───────────── */}
      {actions && (
        <div
          onClick={(e) => e.stopPropagation()}
          style={{
            flexShrink: 0,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'flex-end',
            justifyContent: 'center',
            gap: '6px',
          }}
        >
          <ConsultsVitalsCell bookedSlot={bookedSlot} onOpenVitals={onOpenVitals} />
          <div style={{ display: 'flex', alignItems: 'center' }}>{actions}</div>
        </div>
      )}

      {/* ── Wait time: corner tag complementing the left rail ────────────────────────────────── */}
      {showWait && (
        <span
          style={{
            position: 'absolute',
            bottom: 0,
            right: 0,
            display: 'inline-flex',
            alignItems: 'center',
            gap: '4px',
            fontSize: '11px',
            fontWeight: 700,
            color: pickTextColor(railColor) === '#ffffff' ? railColor : '#334155',
            background: hexToRgba(railColor, 0.14),
            borderTop: `1px solid ${hexToRgba(railColor, 0.35)}`,
            borderLeft: `1px solid ${hexToRgba(railColor, 0.35)}`,
            borderTopLeftRadius: '10px',
            padding: '2px 9px',
            whiteSpace: 'nowrap',
          }}
          title="Patient waiting time"
        >
          <AccessTimeRoundedIcon sx={{ fontSize: 13 }} />
          Wait {waitContent}
        </span>
      )}
    </div>
  )
}
